/** Data type of policy title*/

import { Beneficiary } from './EventorMessageTransformationTypes';
import { AgentInfo, Designation, Follower } from './Types';
import { UnderwriterDetail, UWActivityJSON } from '../../../../common/js/uwService/Types';
import { ExamStatus } from '@hit/underwriting-api-client';

export enum PolicyType {
  Primary = 'primary',
  Secondary = 'secondary',
}

/**
 * What the case is refered to at
 * a given point in its lifecycle.
 *
 * Usually line up with Case Badges.
 *
 * Used to group cases on the List View.
 */
export enum CaseBucket {
  Application = 'Application',
  Offer = 'Offer',
  Policy = 'Policy',
  Quote = 'Quote',
}

/**
 * Badge that shows high level progress
 * of the case on the Insured Bar.
 */
export enum CaseBadge {
  PreSubmit = 'PreSubmit',
  Pending = 'Pending',
  Approved = 'Approved',
  Declined = 'Declined',
  Canceled = 'Canceled',
  InForceGrace = 'InForceGrace',
  InForce = 'InForce',
  Terminated = 'Terminated',
  Other = '',
}

/**
 * Stages of the Case Tracker.
 */
export enum CaseTrackerStage {
  Application = 'application',
  Signature = 'signature',
  Exam = 'exam',
  Underwriting = 'underwriting',
  Issuance = 'issuance',
}

/**
 * Phases of each Case Tracker stage.
 */
export enum CaseTrackerPhase {
  NotStarted = 'not started',
  Skipped = 'skipped',
  InProgress = 'in progress',
  Complete = 'complete',
}

/**
 * Mapping of each Case Tracker stage
 * to the Case Tracker phases.
 */
export type CaseTrackerStagePhaseMap = {
  [CaseTrackerStage.Application]: CaseTrackerPhase;
  [CaseTrackerStage.Signature]: CaseTrackerPhase;
  [CaseTrackerStage.Exam]: CaseTrackerPhase;
  [CaseTrackerStage.Underwriting]: CaseTrackerPhase;
  [CaseTrackerStage.Issuance]: CaseTrackerPhase;
};

/**
 * Labels used in the tabs of the
 * Detail View.
 */
export enum TabHeader {
  InsuredOwnership = 'Insured & Ownership',
  Beneficiaries = 'Beneficiaries',
  OtherPolicies = 'Other policies',
  CommissionSplit = 'Commission split',
}

/**
 * Owner name and email
 */
export type Owner = {
  firstName: string;
  lastName: string;
  email: string;
};

export type CaseDetailsData = {
  activities?: Promise<[string, Activity[]][]>; //Array<any>; // TODO: update with better type (future work)
  alertBanners?: Array<AlertBanner>; // TODO: update with better type (future work)
  badge?;
  beneficiaries?;
  clients?;
  followers?: Array<Follower>;
  followingCase: boolean;
  hasSociety1851: boolean;
  insuredFirstName: '';
  insuredMiddleName: '';
  insuredLastName: '';
  owners: Array<Owner>;
  ownerTitle: string;
  policyId?: string;
  policyNumbers: Array<string>; // TODO: handle multiple policy numbers ticket (HL-72066)
  premiumBreakdown?: Record<string, string>; // TODO: update/remove with real data, also could add all available riders to record key type instead of string
  primaryProducer: AgentInfo;
  staticBanners?: Promise<Array<StaticBanner>>; // TODO: update with better type (future work)
  tabs?: Array<string>;
  trackerStages?;
  nextSteps?: Array<any>; // TODO: update with better type (future work)
};

export type CaseDetailsDetail = {
  label: string;
  value: string;
};

export type CaseDetailsRider = {
  label: string;
  riderDetails?: Array<CaseDetailsDetail>;
};

export type Tab = {
  name: string;
  data: any;
};

export type CaseDocuments = Array<{
  link: string;
  linkIcon: string;
  action: () => void;
  type?: string;
  disabled?: boolean;
  options?: Array<{
    name: string;
    action: () => void;
  }>;
  tooltip?: string;
  note?: string;
} | null>;

export type CaseDetailsEntityDetails = {
  Type: 'Trust' | 'Entity';
  Address?: string;
};

export type CaseDetailsBeneficiary = {
  name: string;
  percentage: number;
  details?: Beneficiary | CaseDetailsEntityDetails;
};

export enum Download {
  Illustration = 'Illustration',
  InforceIllustration = 'Inforce illustration',
  PolicyPacket = 'Policy packet',
  ApplicationPacket = 'Application packet',
  CreateIllustration = 'Create illustration',
  InforceIllustrationOption = 'Inforce illustration',
  ExerciseGirOption = 'Exercise GIR',
  ChangeFaceAmountOption = 'Change face amount',
}

export enum NextStepPhase {
  Completed = 'Completed',
  Current = 'Current',
  Upcoming = 'Upcoming',
}

export enum NextStepPhaseOrder {
  Completed = 1,
  Current = 2,
  Upcoming = 3,
}
export type NextStep = {
  name: string;
  hintText: string;
  type: NextStepPhase;
  stage: CaseTrackerStage;
  actor?: any;
  CTAs?: Array<CTA>;
  isExpiring?;
  dueDate?;
  examOrder?;
  canReoccur?;
};

export type CTA = {
  id: string;
  name: string;
  showSpinner?: boolean;
  action: () => void;
  type: string;
  disabled?: boolean;
  hasTooltip?: boolean;
  tooltipText?: string;
  isScheduleExam?: boolean;
  examVendor?: string;
};

export enum CTAType {
  Primary = 'Primary',
  Secondary = 'Secondary',
}

export enum BusinessUnit {
  MmfaCareer = 'MmfaCareer',
  ThirdParty = 'ThirdParty',
  Manual = 'Manual',
  MmfaBrokerage = 'MmfaBrokerage',
}

export interface CaseDetailsCommission {
  identity?: string;
  advisorDisplayText?: string;
  ratio: number | string;
  renewalCommissionRatio: number | string; // string only empty string to indicate show nothing on commissionSplit
  profile?: AgentInfo;
  designations?: Array<Designation>;
}

export enum GridLabel {
  PRODUCER = 'Producer',
  FIRST_YEAR = 'First Year',
  RENEWAL_YEAR = 'Renewal Year',
}

export enum GridLabelInactive {
  PRODUCER = 'Producer',
  INACTIVE = '',
  FIRST_YEAR = 'First Year',
  RENEWAL_YEAR = 'Renewal Year',
}

export enum OtherPoliciesGridLabel {
  INSURED = 'Insured',
  POLICY = 'Policy',
  POLICY_NUMBER = 'Policy number',
}

export enum TrackerStatus {
  Application = 'Application',
  Signature = 'Signature',
  Exam = 'Exam',
  Underwriting = 'Underwriting',
  Issuance = 'Issuance',
  OfferNotTaken = 'Offer not taken',
  ApplicationDeclined = 'Application declined',
  ApplicationRanOutOfTime = 'Application ran out of time',
  ApplicationCanceledByClient = 'Application canceled by client',
  ApplicationRejected = 'Application rejected',
  OfferRescinded = 'Offer rescinded',
  CoveragePending = 'Coverage pending - Initial premium payment failed',
  SettingUpPolicyDocuments = 'Setting up policy documents',
  InForceFreeLook = 'In force - Free look ends [MM/DD] at [##:##pm]',
  InForce = 'In force',
  Grace = 'Grace',
  PolicyLapsed = 'Policy lapsed',
  PolicyRescinded = 'Policy rescinded',
  DeathClaimPending = 'Death claim pending',
  DeathClaimPaid = 'Death claim paid',
  PolicyCanceledBySystem = 'Policy canceled by system',
  PolicyCanceledByClient = 'Policy canceled by client',
  PolicyCanceledAfterConversion = 'Policy canceled after conversion',
  CouldNotLoadStatus = 'Could not load status: Please try again later or contact support',
}

export enum StageCode {
  /**
   * TLIC Confirm Issue
   */
  TLICConfirmIssue = 'TC1',
  /**
   * Knockout
   */
  Knockout = 'MA1',
  /**
   * Fixing ID Data (both)
   */
  FixingIDDataForBoth = 'FI1',
  /**
   * Fixing Insured ID Data
   */
  FixingInsuredIDData = 'FI2',
  /**
   * Fixing Owner ID Data
   */
  FixingOwnerIDData = 'FI3',
  /**
   * Fixing MVR Data
   */
  FixingMVRData = 'FL1',
  /**
   * Advisor Decline Review: Waiting for Advisor
   */
  AdvisorDeclineReview = 'AD1',
  /**
   * Confirm Underwriting Consent
   */
  ConfirmUnderwritingConsent = 'IC1',
  // Advisor Amendment
  AdvisorAmendment = 'AM4',
  SendApplicationToDocusignKnockout = 'MA2',
  SendApplicationToDocusignTLIC = 'TC2',
  VerifyProducerLicense = 'CS2',
  VerifyProducerLicense2 = 'CS3',
  /**
   * Issue Certificate: Waiting for Labs
   */
  TLICWaitingForLabs = 'TC3',
  ScheduleParamed = 'MA3',
  ConfirmAcceleratedIssue = 'AI1',
  ConfirmIssuePolicyManual = 'MI1',
  ConfirmIssuePolicyAlgo = 'LI1',
  SendToDocusign = 'AI2',
  /**
   * Fluidless
   */
  Fluidless = 'AM4',
  PolicyRejectedHaven = 'DN1',
  PolicyRejectedUninsurable = 'DN3',
  PolicyRejectedVantage = 'DN2',
  PolicyDeclinedVantage = 'DN4',
  PolicyDeclinedUninsurable = 'DN5',
  ApplicationCanceledByClient = 'CC1',
  RanOutOfTimeApp = 'CC2',
  RanOutOfTimeUW = 'CC3',
  TimedOutApp = 'FC1',
  AppCanceledPreIssuance = 'CC4',
  Underwriting = 'UW2',
}

export enum UWStage {
  /**
   * SL1 Schedule Labs
   */
  ScheduleLabs = 'SL1 Schedule Labs',
  FinalAlgorithmicUnderwriting = 'FU1 Final Algorithmic Underwriting',
  FinalUnderwiterReview = 'MU1 Final Underwriter Review',
}

export enum ActivityName {
  CancelDataLicenseNotFixed = 'FL1A3 Email Cancel Data Not Fixed',
  CancelDataIdNotFixedForBoth = 'FI1A3 Email Cancel Data Not Fixed',
  CancelDataInsuredIdNotFixed = 'FI2A3 Email Cancel Data Not Fixed',
  CancelDataOwnerIdNotFixed = 'FI3A3 Email Cancel Data Not Fixed',
  CancelApplicationNotConfirmed = 'MA2A4 Cancel Application Not Confirmed',
  CancelTlicNoSignature = 'TC2A5 Cancel TLIC No Signature',
  TlicAutoDeclineTimeout = 'TC1A10 TLIC Offer Auto Decline on Timeout',
  HoldOfferDeclined = 'AD1A1 Hold - Declined Offer',
  CancelAcceleratedNoSignature = 'AI2A4 Cancel Accelerated No Signature',
  CancelAcceleratedNotConfirmed = 'AI1A3 Cancel Accelerated Not Confirmed',
  CancelPolicyNotConfirmedManual = 'MI1A3 Cancel Policy Not Confirmed',
  CancelPolicyNotConfirmedAlgo = 'LI1A2 Cancel Policy Not Confirmed',
  VerifyAgentLicenseFU = 'UW2A21 Verify agent license',
  VerifyAgentLicenseMU = 'UW2A27 Verify agent license',
  VerifyAgentLicenseAU = 'UW2A55 Verify agent license',
}

export enum RequirementCode {
  /**
   * IC1R4 Insured consent has been received
   */
  InsuredConsentReceived = 'IC1R4',
  /**
   * IC1R4 Owner consent has been received
   */
  OwnerConsentReceived = 'IC1R7',
  /**
   * TC3R5 Received lab results
   */
  ReceivedLabResults = 'TC3R5',
  AcceleratedUnderwritingOfferReady = 'UW2R6',
  FinalAlgoOfferReady = 'UW2R11',
  FinalManualOfferReady = 'UW2R21',
  /**
   * MI1R1 Policy issue has been confirmed.
   * Manual underwriting flow.
   */
  PolicyIssueConfirmedManual = 'MI1R1',
  /**
   * Insured finished Esign.
   * Manual underwriting flow.
   */
  InsuredFinishedEsignManual = 'MI1R13',
  /**
   * MI1R1 Policy issue has been confirmed.
   * Algo underwriting flow.
   */
  PolicyIssueConfirmedAlgo = 'LI1R4',
  /**
   * Insured finished Esign.
   * Algo underwriting flow.
   */
  InsuredFinishedEsignAlgo = 'LI1R14',
  /**
   * AI1R2 Policy issue has been confirmed.
   * This represents owner acceptance in the
   * fluidless express path offer flow.
   */
  PolicyIssueConfirmedFluidless = 'AI1R2',
  /**
   * AD1R1 Decline letter sent
   */
  DeclineLetterSent = 'AD1R1',
}

export const expiringIfLessThanDays = 3;

export type KebabActionDefinition = {
  actionType: KebabActionTypes;
  displayText: string;
  subText?: string;
  icon?: string;
  action?: () => void;
};

export enum KebabActionTypes {
  DELETE = 'delete',
  COPY_LINK = 'copyLink',
  VIEW_APPLICATION = 'viewApplication',
  COPY_APPLICATION = 'copyApplication',
  EDIT_APPLICATION = 'editApplication',
  FOLLOWING = 'following',
}

export enum CashSurrenderLabel {
  AMOUNT = 'Amount',
  CASH_VALUE_PUA = 'Cash value from paid-up additions',
  DIVIDEND = 'Dividend accumulations',
  GROSS_CASH_VALUE = 'Gross cash value',
  GUARANTEED_CASH_VALUE = 'Guaranteed cash value',
  OUTSTANDING_LOAN_INTEREST = 'Outstanding loan + interest',
  UNEARNED_PREMIUM = 'Unearned premium',
}

export enum ClientTitle {
  Insured = 'Insured',
  JointOwner = 'Joint Owner',
  Owner = 'Owner',
  ContingentOwner = 'Contingent Owner',
  Signer = 'Signer',
  AccountHolder = 'Account Holder',
  InsuredJointOwnerAccountHolder = 'Insured, Joint Owner & Account Holder',
  InsuredOwnerAccountHolder = 'Insured, Owner & Account Holder',
  JointOwnerAccountHolder = 'Joint Owner & Account Holder',
  OwnerAccountHolder = 'Owner & Account Holder',
  SignerAccountHolder = 'Signer & Account Holder',
}

export const TrustOrOtherEntityTypeMap = {
  trust: 'Trust',
  incorporatedEntity: 'Business',
  nonIncorporatedEntity: 'Non-incorporated Entity',
};

export interface AllActivities {
  uwActivities?: UWActivities;
  examStatuses?: ExamStatuses;
}

export type ExamStatuses = {
  orderNumber?: string | null;
  examVendor?: string | null;
  data?: Array<ExamStatus> | null;
};

/**
 * Phases of each Case Tracker stage.
 */
export enum CPDSTrackerStatus {
  Future = 'future',
  Skipped = 'skipped',
  Current = 'current',
  Completed = 'completed',
}
export interface UWActivities {
  data?: Array<UWActivityJSON>;
  underwriter?: UnderwriterDetail;
  followUps?: Array<UWActivityJSON>;
  apsOrders?: Array<UWActivityJSON>;
  digitalServiceRequests?: Array<UWActivityJSON>;
}

export interface AllActivities {
  uwActivities?: UWActivities;
  examStatuses?: ExamStatuses;
}

export type Activity = {
  activityDate: string;
  activityDescriptionHeader?: string;
  activityDescription: string;
  activityTime: string;
  activityTitle: string;
  avatar: {
    name: string;
    type: string;
    tooltip: string;
  };
  daysAgo: string;
  group?: boolean;
  createdDate?: Date;
};

export type ActivitiesMap = Map<string, Array<Activity>>;

export const Assignee = {
  underwriter: {
    type: 'underwriter',
    name: 'Underwriter',
    tooltip: 'Underwriter',
  },
  underwriting: {
    type: 'underwriting',
    name: 'Underwriting',
    tooltip: 'Underwriting',
  },
  coverpath: {
    type: 'mm-coverpath',
    name: 'Coverpath',
    tooltip: 'Coverpath',
  },
  examvendor: {
    type: 'medical',
    name: 'Exam vendor',
    tooltip: 'Exam vendor',
  },
  apsvendor: {
    type: 'parameds',
    name: 'APS vendor',
    tooltip: 'APS vendor',
  },
  client: {
    type: 'client',
    name: '[Client]',
    tooltip: 'Client',
  },
  digitalservices: {
    type: 'digital-services',
    name: 'Digital Services',
    tooltip: 'Digital Services',
  },
  producer: {
    type: 'producer',
    name: '[Producer]',
    tooltip: 'Producer',
  },
};

export enum RiderDetailLabel {
  AMOUNT = 'Amount',
  ANNUAL_MAXIMUM = 'Annual maximum',
  APPLY_TOWARD = 'Apply Toward',
  CONVERSION_CREDIT = 'Conversion credit',
  COST_BASIS = 'Cost Basis',
  DURATION = 'Duration',
  END_DATE = 'End date',
  PAID_UP_ADDITIONS = 'Paid-up additions',
  PAYMENT_SCHEDULE = 'Payment schedule',
  PAYOUT_METHOD = 'Payout method',
  STATUS = 'Status',
}

export enum RiderLabel {
  ADBR = 'Accelerated Death Benefit',
  ALIR = 'Additional Life Insurance Rider',
  CIR = 'Chronic Illness Rider',
  GIR = 'Guaranteed Insurability Rider',
  RTR = 'Renewable Term Rider',
  TIR = 'Transfer of Insured',
  WOP = 'Waiver of Premium',
}

export enum RiderLabelAbbrev {
  ALIR = 'ALIR',
  CIR = 'CIR',
  GIR = 'GIR',
  RTR = 'RTR',
  TIR = 'TIR',
}

export type SideBarProductSharedData = {
  primary: {
    riders: Array<CaseDetailsRider>;
    sidebarRiders: string;
    premium: string;
    productDescription: string;
    sidebarProductDescription: string;
    faceAmount: string;
    rateClassDesc?: string;
  };
  secondary?: {
    riders: Array<CaseDetailsRider>;
    sidebarRiders: string;
    premium: string;
    productDescription: string;
    sidebarProductDescription: string;
    faceAmount: string;
    rateClassDesc?: string;
  };
  ownerTitle: string;
  owners: Array<Owner>;
  totalPremium: string;
  caseBucket: CaseBucket;
};

export type CaseDetailsSideBarData = {
  primary: {
    riders: string;
    premium: string;
    productDescription: string;
    faceAmount: string;
  };
  secondary?: {
    riders: string;
    premium: string;
    productDescription: string;
    faceAmount: string;
  };
  ownerTitle: string;
  owners: Array<Owner>;
  totalPremium: string;
  caseBucket: CaseBucket;
  hasSociety1851: boolean;
  primaryProducer: AgentInfo;
  followers: Array<Follower>;
  editOffer?: {
    text: string;
    action?: () => void;
  };
  rateClassDesc: string;
};

export enum ActorType {
  Owner = 'Owner',
  Insured = 'Insured',
}

export type AlertBanner = {
  intent: string;
  intentIcon?: string;
  header: string;
  secondRow?: string;
};

export enum AlertBannerIntent {
  Danger = 'danger',
  Information = 'information',
}
export type StaticBanner = {
  icon: string;
  message: string;
};
